import { create } from 'zustand'
import { onlineManager } from '@tanstack/react-query'

/**
 * Passive connection-health tracking. Zero probing: every signal is
 * harvested from traffic the app already generates.
 *
 *   - API outcomes + durations (axios interceptors in api/client.ts)
 *   - the existing 2-minute /version.json update poll (UpdateNotice)
 *   - Chromium's passive network-quality estimate (navigator.connection.rtt
 *     -- dependable here because Electron pins the Chromium version)
 *   - the browser's own online/offline events
 *   - the captive-portal detector (HTML interstitial, api/client.ts)
 *
 * Derived UI state:
 *   offline  -> red "Offline Mode" pill (unchanged look)
 *   degraded -> amber "Slow Connection" pill
 *   online   -> nothing
 *
 * Attribution (reason) is TELEMETRY-ONLY in v1: it rides the transition
 * console line (which ships to PostHog session replay) so support can see
 * "offline(server)" vs "offline(internet)" vs "offline(captive)", but the
 * cashier-facing copy stays the two pills.
 *
 * Deliberate asymmetry: offline is a discrete last-known fact and stays
 * until contrary evidence (an answered request, or the browser's online
 * event). Degraded is a continuous measurement and DECAYS -- it requires
 * traffic evidence fresher than EVIDENCE_FRESH_MS, so an idle register
 * drifts back to green rather than pinning a stale yellow. Idle blindness
 * both ways is the accepted trade for zero probing.
 *
 * One exception to zero probing: the browser's "offline" is a claim, not
 * evidence. Chrome on macOS can keep navigator.onLine false after a
 * sleep/wake or a VPN adapter change while every request still succeeds
 * -- and since react-query pauses on it, Sign In then spins forever
 * without sending anything. So while the browser says offline, a status
 * probe (injected via initConnectionHealth) asks the backend every
 * REEVAL_INTERVAL_MS; any HTTP answer, like any answered API request,
 * overrules the browser.
 */

// --- Tunables (expect first-week calibration; keep them together) ----------
const RTT_TRIP_MS = 600        // Chromium rtt above this (sustained) trips yellow
const RTT_CLEAR_MS = 400       // dual threshold: below this, rtt is considered clear
const EWMA_TRIP_MS = 3000      // smoothed API duration above this trips yellow
const EWMA_CLEAR_MS = 1500
const EWMA_ALPHA = 0.3         // weight of newest latency sample
const MIN_LATENCY_SAMPLES = 3  // EWMA can't trip until it has this many samples
const DEGRADED_DWELL_MS = 10_000   // trip condition must persist this long
const EVIDENCE_FRESH_MS = 60_000   // yellow needs traffic newer than this
const FAILURES_TO_OFFLINE = 2      // consecutive network failures => red
const CAPTIVE_FRESH_MS = 60_000    // interstitial sighting counts this long
const VERSION_PROBE_FRESH_MS = 3 * 60_000  // version poll outcome usable this long
const REEVAL_INTERVAL_MS = 10_000  // local recompute tick (no network)

export type ConnectionStatus = 'online' | 'degraded' | 'offline'
export type OfflineReason = 'network' | 'server' | 'internet' | 'captive' | null

interface ConnectionHealthState {
  status: ConnectionStatus
  reason: OfflineReason
  browserOnline: boolean

  // Internals -- exposed for debugging and the transition log.
  consecutiveFailures: number
  lastSuccessAt: number | null
  lastFailureAt: number | null
  // Freshness anchor for the degraded verdict: bumped ONLY by real API
  // latency samples. Version-poll heartbeats and failures deliberately
  // don't touch it -- otherwise a stale high EWMA would be resurrected
  // as "fresh" every 2 minutes by the update poll with zero new
  // latency evidence.
  lastLatencySampleAt: number | null
  durationEwmaMs: number | null
  latencySampleCount: number
  captiveSeenAt: number | null
  versionProbe: { ok: boolean; at: number } | null
  degradedCandidateSince: number | null

  recordApiSuccess: (durationMs: number | null) => void
  recordApiNetworkFailure: () => void
  recordCaptivePortal: () => void
  recordVersionProbe: (ok: boolean) => void
  _setBrowserOnline: (online: boolean) => void
  _verifyBrowserOffline: () => void
  _reevaluate: () => void
}

// Resolves true when the backend answered at all (any HTTP status), false
// on a network-level failure. Must not reject. Set by initConnectionHealth.
export type ReachabilityProbe = () => Promise<boolean>

let reachabilityProbe: ReachabilityProbe | null = null
let probeInFlight = false

function readChromiumRtt(): number | null {
  if (typeof navigator === 'undefined') return null
  const conn = (navigator as unknown as {
    connection?: { rtt?: unknown }
  }).connection
  const rtt = conn?.rtt
  // Some Electron/Chromium builds report rtt 0 when the estimator has no
  // samples -- treat 0/absent as "no data", never as "fast".
  return typeof rtt === 'number' && rtt > 0 ? rtt : null
}

export const useConnectionHealthStore = create<ConnectionHealthState>((set, get) => {
  const derive = () => {
    const s = get()
    const now = Date.now()

    let status: ConnectionStatus = 'online'
    let reason: OfflineReason = null
    let candidateSince = s.degradedCandidateSince

    const captiveFresh =
      s.captiveSeenAt !== null
      && now - s.captiveSeenAt <= CAPTIVE_FRESH_MS
      && (s.lastSuccessAt === null || s.lastSuccessAt < s.captiveSeenAt)

    const failedSinceLastSuccess =
      s.consecutiveFailures >= FAILURES_TO_OFFLINE
      && (s.lastSuccessAt === null
        || (s.lastFailureAt !== null && s.lastSuccessAt < s.lastFailureAt))

    if (!s.browserOnline) {
      status = 'offline'
      reason = 'network'
    } else if (captiveFresh) {
      status = 'offline'
      reason = 'captive'
    } else if (failedSinceLastSuccess) {
      status = 'offline'
      const probe = s.versionProbe
      const probeFresh = probe !== null && now - probe.at <= VERSION_PROBE_FRESH_MS
      // Backend unreachable + CDN reachable => the internet is fine and
      // the server (or the path to it) is the problem. Both failing =>
      // the uplink itself. No usable probe => unattributed.
      reason = probeFresh ? (probe.ok ? 'server' : 'internet') : null
    } else {
      // Yellow: latency-based, evidence-fresh, dwell-gated.
      const rtt = readChromiumRtt()
      const fresh =
        s.lastLatencySampleAt !== null
        && now - s.lastLatencySampleAt <= EVIDENCE_FRESH_MS
      const ewmaUsable = s.durationEwmaMs !== null && s.latencySampleCount >= MIN_LATENCY_SAMPLES
      const tripping =
        fresh
        && ((rtt !== null && rtt > RTT_TRIP_MS)
          || (ewmaUsable && (s.durationEwmaMs as number) > EWMA_TRIP_MS))
      const clear =
        (rtt === null || rtt <= RTT_CLEAR_MS)
        && (!ewmaUsable || (s.durationEwmaMs as number) <= EWMA_CLEAR_MS)

      if (s.status === 'degraded') {
        // Stay yellow until measurements clear or the evidence goes stale.
        status = (clear || !fresh) ? 'online' : 'degraded'
        if (status === 'online') candidateSince = null
      } else if (tripping) {
        if (candidateSince === null) {
          candidateSince = now
          status = 'online'
        } else if (now - candidateSince >= DEGRADED_DWELL_MS) {
          status = 'degraded'
        }
      } else {
        candidateSince = null
      }
    }

    if (status !== 'degraded' && status !== 'online') candidateSince = null

    if (status !== s.status || reason !== s.reason) {
      // One line per transition; ships to PostHog replay -- this is the
      // support-facing record of "Slow Connection 14:02-14:07".
      console.info(
        '[Connection] %s -> %s%s',
        s.status,
        status,
        reason ? ` (${reason})` : '',
        {
          rtt: readChromiumRtt(),
          ewmaMs: s.durationEwmaMs === null ? null : Math.round(s.durationEwmaMs),
          consecutiveFailures: s.consecutiveFailures,
        },
      )
    }
    set({ status, reason, degradedCandidateSince: candidateSince })
  }

  // A server answer proves the network is up, whatever navigator.onLine
  // says. Callers derive() afterwards.
  const overruleBrowserOffline = (evidence: string) => {
    if (get().browserOnline) return
    console.info('[Connection] browser reports offline but %s answered; treating as online', evidence)
    set({ browserOnline: true })
    onlineManager.setOnline(true)
  }

  return {
    status: 'online',
    reason: null,
    browserOnline: typeof navigator === 'undefined' ? true : navigator.onLine,
    consecutiveFailures: 0,
    lastSuccessAt: null,
    lastFailureAt: null,
    lastLatencySampleAt: null,
    durationEwmaMs: null,
    latencySampleCount: 0,
    captiveSeenAt: null,
    versionProbe: null,
    degradedCandidateSince: null,

    // durationMs null = reachability-only evidence (non-2xx responses,
    // retried requests whose timing spans auth round-trips) -- proves the
    // server answered without polluting the latency EWMA.
    recordApiSuccess: (durationMs) => {
      const now = Date.now()
      set((s) => ({
        consecutiveFailures: 0,
        lastSuccessAt: now,
        ...(durationMs !== null
          ? {
              lastLatencySampleAt: now,
              durationEwmaMs:
                s.durationEwmaMs === null
                  ? durationMs
                  : s.durationEwmaMs + EWMA_ALPHA * (durationMs - s.durationEwmaMs),
              latencySampleCount: s.latencySampleCount + 1,
            }
          : {}),
      }))
      overruleBrowserOffline('an API request')
      derive()
    },

    recordApiNetworkFailure: () => {
      const now = Date.now()
      set((s) => ({
        consecutiveFailures: s.consecutiveFailures + 1,
        lastFailureAt: now,
      }))
      derive()
    },

    recordCaptivePortal: () => {
      set({ captiveSeenAt: Date.now() })
      derive()
    },

    // Offline reason attribution ONLY (server vs internet) -- never
    // latency-freshness evidence; see lastLatencySampleAt.
    recordVersionProbe: (ok) => {
      set({ versionProbe: { ok, at: Date.now() } })
      derive()
    },

    _setBrowserOnline: (online) => {
      set({ browserOnline: online })
      // react-query's pause/resume follows the BROWSER's own signal (as
      // overruled by a server answer, see overruleBrowserOffline). Our
      // richer states stay presentational -- pausing queries on
      // backend-unreachable would starve the very traffic that detects
      // recovery.
      onlineManager.setOnline(online)
      derive()
      if (!online) get()._verifyBrowserOffline()
    },

    // Ask the backend whether the browser's "offline" is real. No-op while
    // the browser says online, so the zero-probing rule holds there.
    _verifyBrowserOffline: () => {
      if (get().browserOnline || probeInFlight || reachabilityProbe === null) return
      probeInFlight = true
      void reachabilityProbe()
        .catch(() => false)
        .then((answered) => {
          probeInFlight = false
          if (answered) {
            overruleBrowserOffline('the status probe')
            derive()
          }
        })
    },

    _reevaluate: () => derive(),
  }
})

/**
 * Install the store's listeners exactly once (call from main.tsx).
 * Window-flag guarded so Vite HMR can't double-register.
 */
export function initConnectionHealth(
  options: { probeReachability?: ReachabilityProbe } = {},
): void {
  if (typeof window === 'undefined') return
  const w = window as unknown as Record<string, unknown>
  if (w.__waioConnHealthInit === true) return
  w.__waioConnHealthInit = true
  reachabilityProbe = options.probeReachability ?? null

  const store = useConnectionHealthStore.getState()
  window.addEventListener('online', () => store._setBrowserOnline(true))
  window.addEventListener('offline', () => store._setBrowserOnline(false))

  const conn = (navigator as unknown as {
    connection?: { addEventListener?: (t: string, cb: () => void) => void }
  }).connection
  conn?.addEventListener?.('change', () => store._reevaluate())

  // Local recompute -- matures the degraded dwell, decays stale yellow.
  // Touches the network only while the browser claims offline (the status
  // probe), so a stale navigator.onLine can't pin the register offline.
  window.setInterval(() => {
    store._reevaluate()
    store._verifyBrowserOffline()
  }, REEVAL_INTERVAL_MS)

  // Derive immediately so a register booted with the interface already
  // down shows red at first paint instead of waiting for the first tick.
  store._setBrowserOnline(navigator.onLine)
}
